import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CaretLeft, CaretRight, CircleNotch, ArrowClockwise, PaperPlaneTilt,
} from '@phosphor-icons/react';

import './onboarding.css';
import { useOnboardingDraft } from './useOnboardingDraft';
import { onboardingApi } from './api';
import { STEP_COMPONENTS, CONTINUE_LABELS, MILESTONES } from './stepRegistry';
import { SavedIndicator, ProgressBar, ProfileRail, MilestoneToast } from './components/layout';
import { resolveUploadUrl } from './api';
import { BRAND_NAME, BRAND_LOGO } from '../../config/brand';

function resolveLogoUrl(url) {
  return resolveUploadUrl(url);
}

/**
 * After saving a step, skip already-complete sections and land on the next
 * open one — or Review when everything required ahead is done. That way a
 * person who jumped back from Review to fix Contact returns straight to the
 * next problem (or Review), instead of walking the whole form again.
 */
function resolveNextStepKey(steps, fromIndex, sections) {
  const isDone = (s) =>
    !s.collectsData
    || Boolean(sections[s.key]?.is_complete)
    || Boolean(sections[s.key]?.skipped);

  for (let i = fromIndex + 1; i < steps.length; i += 1) {
    const s = steps[i];
    if (s.key === 'review') return 'review';
    if (s.key === 'complete') continue;
    if (!isDone(s)) return s.key;
  }

  for (let i = 0; i < fromIndex; i += 1) {
    const s = steps[i];
    if (s.collectsData && !s.optional && !isDone(s)) return s.key;
  }

  const review = steps.find((s) => s.key === 'review');
  return review?.key || steps[fromIndex + 1]?.key || null;
}

/**
 * Orchestrates the journey: which step is showing, moving between them, and
 * wiring each step to the draft. Step components stay presentational — they
 * receive their slice of data and an update function, and know nothing about
 * navigation or persistence.
 */
export default function OnboardingJourney() {
  const draft = useOnboardingDraft();
  const {
    status, loadError, config, token, profile, sections, documents,
    completion, saveState, saveError, sectionErrors,
    getSectionData, updateSection, commitSection, revealSectionGaps, skipSection,
    uploadPhoto, removePhoto, uploadDocument, removeDocument, submit,
  } = draft;

  const [stepKey, setStepKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [milestone, setMilestone] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [applicationId, setApplicationId] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('pending_review');
  const [reviewKey, setReviewKey] = useState(0);
  const [resumePanel, setResumePanel] = useState(null);

  const topRef = useRef(null);
  const startedAtRef = useRef(null);

  const steps = config?.sections || [];
  const dataSteps = useMemo(() => steps.filter((s) => s.collectsData), [steps]);

  // Resume at wherever they got to, once the draft has loaded.
  useEffect(() => {
    if (status !== 'ready' || stepKey || !profile) return;
    const resumeAt = ['submitted', 'pending_review', 'accepted'].includes(profile.status)
      ? 'complete'
      : profile.current_step || 'welcome';
    setStepKey(resumeAt);
    startedAtRef.current = resumeAt;
  }, [status, stepKey, profile]);

  const index = steps.findIndex((s) => s.key === stepKey);
  const step = steps[index];
  const StepComponent = stepKey ? STEP_COMPONENTS[stepKey] : null;

  const scrollToTop = useCallback(() => {
    if (topRef.current) topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const scrollToFirstMissing = useCallback(() => {
    window.setTimeout(() => {
      const field = document.querySelector('.ob-body .ob-field[data-missing="true"]');
      const control = field?.querySelector('input, textarea, select, button');
      if (field) {
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        field.classList.add('ob-field-flash');
        window.setTimeout(() => field.classList.remove('ob-field-flash'), 1600);
      }
      if (control && typeof control.focus === 'function') {
        try { control.focus({ preventScroll: true }); } catch { control.focus(); }
      }
    }, 80);
  }, []);

  const goTo = useCallback((key) => {
    setStepKey(key);
    setSubmitError(null);
    if (key === 'review') setReviewKey((k) => k + 1);
    requestAnimationFrame(scrollToTop);
  }, [scrollToTop]);

  /** Jump to a section and highlight any fields still missing. */
  const goToFix = useCallback(async (key) => {
    const meta = steps.find((s) => s.key === key);
    if (meta?.collectsData) {
      await revealSectionGaps(key);
    }
    setStepKey(key);
    setSubmitError(null);
    requestAnimationFrame(() => {
      scrollToFirstMissing();
    });
  }, [steps, revealSectionGaps, scrollToFirstMissing]);

  const showMilestone = useCallback((key) => {
    const message = MILESTONES[key];
    if (!message) return;
    setMilestone(message);
    setTimeout(() => setMilestone(null), 2600);
  }, []);

  // --- navigation -------------------------------------------------------
  const handleBack = () => {
    if (index > 0) goTo(steps[index - 1].key);
  };

  const handleContinue = async () => {
    if (!step) return;
    setBusy(true);
    setSubmitError(null);

    try {
      if (step.key === 'review') {
        const result = await submit();
        if (!result.ok) {
          const payload = result.error?.payload || {};
          if (Array.isArray(payload.outstanding) && payload.outstanding.length) {
            setSubmitError(
              `${result.error.message} Still to finish: ${payload.outstanding.map((o) => o.label).join(', ')}.`
            );
            // Jump straight to the first section that still needs work.
            await goToFix(payload.outstanding[0].key);
          } else {
            setSubmitError(result.error?.message || 'We could not send that just now.');
            setReviewKey((k) => k + 1);
          }
          return;
        }
        setApplicationId(result.applicationId);
        setReviewStatus(result.status || 'pending_review');
        goTo('complete');
        return;
      }

      let sectionsAfter = sections;
      if (step.collectsData) {
        const result = await commitSection(step.key, step.key);
        const hasGaps = result.errors && Object.keys(result.errors).length > 0;
        if (!result.ok || hasGaps || result.isComplete === false) {
          // Errors are rendered inline against the offending fields; nudge the
          // person to the first missing input.
          scrollToFirstMissing();
          return;
        }
        showMilestone(step.key);
        sectionsAfter = {
          ...sections,
          [step.key]: {
            ...(sections[step.key] || {}),
            is_complete: result.isComplete,
            skipped: false,
          },
        };
      }

      const nextKey = resolveNextStepKey(steps, index, sectionsAfter);
      if (nextKey) {
        goTo(nextKey);
        if (nextKey === 'review') setReviewKey((k) => k + 1);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = async () => {
    if (!step?.optional) return;
    setBusy(true);
    try {
      await skipSection(step.key);
      const sectionsAfter = {
        ...sections,
        [step.key]: {
          ...(sections[step.key] || {}),
          is_complete: true,
          skipped: true,
        },
      };
      const nextKey = resolveNextStepKey(steps, index, sectionsAfter);
      if (nextKey) goTo(nextKey);
    } finally {
      setBusy(false);
    }
  };

  // --- save & return -----------------------------------------------------
  const handleResumeLink = async () => {
    const email = getSectionData('contact').email || '';
    setResumePanel({ state: 'sending' });
    try {
      const response = await onboardingApi.requestResumeLink(token, email);
      setResumePanel({ state: 'done', link: response.link, emailed: response.emailed, message: response.message });
    } catch (err) {
      setResumePanel({ state: 'error', message: err.message });
    }
  };

  // --- media wrappers ----------------------------------------------------
  const handlePhoto = async (file) => {
    setPhotoBusy(true);
    const result = await uploadPhoto(file);
    setPhotoBusy(false);
    if (!result.ok) setSubmitError(result.error?.message);
  };

  const handleDocument = async (file, type) => {
    setUploadBusy(true);
    const result = await uploadDocument(file, type);
    setUploadBusy(false);
    if (!result.ok) setSubmitError(result.error?.message);
  };

  // --- states ------------------------------------------------------------
  if (status === 'loading') {
    return (
      <div className="ob-root">
        <div className="ob-center">
          <div>
            <CircleNotch size={30} className="ob-spin" style={{ color: '#4f46e5' }} />
            <p className="ob-hint" style={{ marginTop: 14 }}>Getting your profile ready…</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="ob-root">
        <div className="ob-center">
          <div className="ob-error-card">
            <h2>We could not open your profile</h2>
            <p>{loadError}</p>
            <button
              type="button"
              className="ob-btn ob-btn-primary"
              style={{ width: '100%' }}
              onClick={() => window.location.reload()}
            >
              <ArrowClockwise size={16} weight="bold" />
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!step || !StepComponent) return null;

  const isWelcome = stepKey === 'welcome';
  const isComplete = stepKey === 'complete';
  const isReview = stepKey === 'review';
  const continueLabel = CONTINUE_LABELS[stepKey] || 'Continue';

  const stepProps = {
    data: getSectionData(stepKey),
    update: (patch) => updateSection(stepKey, patch),
    notes: sectionErrors[stepKey] || {},
    config,
    profile,
    documents,
    uploadPhoto: handlePhoto,
    removePhoto,
    uploadDocument: handleDocument,
    removeDocument,
    photoBusy,
    uploadBusy,
    // Review-specific
    token,
    onEdit: goToFix,
    refreshKey: reviewKey,
    // Welcome / complete specific
    resuming: startedAtRef.current !== 'welcome',
    completion,
    applicationId,
    name: profile?.full_name,
    reviewStatus: reviewStatus || profile?.status,
  };

  const campus = profile?.campus || null;
  const campusLogo = resolveLogoUrl(campus?.logo_url);
  const campusTitle = campus?.campus_name || campus?.university_name || null;
  const campusSubtitle = campus?.campus_name && campus?.university_name
    && campus.campus_name !== campus.university_name
    ? campus.university_name
    : campus?.campus_location || null;

  return (
    <div className="ob-root">
      <header className="ob-topbar">
        <div className="ob-topbar-inner">
          <span className="ob-brand">
            {campusLogo ? (
              <img
                src={campusLogo}
                alt={campusTitle || 'Campus'}
                className="ob-brand-logo"
              />
            ) : (
              <img
                src={BRAND_LOGO}
                alt={BRAND_NAME}
                className="ob-brand-logo"
              />
            )}
            <span className="ob-brand-text">
              <span className="ob-brand-name">{campusTitle || BRAND_NAME}</span>
              {campusTitle && campusSubtitle && (
                <span className="ob-brand-sub">{campusSubtitle}</span>
              )}
              {!campusTitle && (
                <span className="ob-brand-sub">Talent Profile</span>
              )}
              {campusTitle && !campusSubtitle && (
                <span className="ob-brand-sub">Talent Profile</span>
              )}
            </span>
          </span>
          <span className="ob-topbar-spacer" />
          {!isComplete && <SavedIndicator state={saveState} error={saveError} />}
        </div>
        {!isWelcome && !isComplete && (
          <ProgressBar percent={completion} label={step.label} />
        )}
      </header>

      <main className="ob-main" ref={topRef}>
        <div>
          {submitError && (
            <div className="ob-outstanding" role="alert">
              <div className="ob-outstanding-title">That did not go through</div>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: '#92400e' }}>
                {submitError}
              </p>
            </div>
          )}

          <StepComponent {...stepProps} />

          {!isWelcome && !isComplete && (
            <div style={{ marginTop: 18, textAlign: 'center' }}>
              {!resumePanel && (
                <button type="button" className="ob-btn ob-btn-text" onClick={handleResumeLink}>
                  Save and come back later
                </button>
              )}

              {resumePanel?.state === 'sending' && (
                <p className="ob-hint">Creating your link…</p>
              )}

              {resumePanel?.state === 'done' && (
                <div className="ob-error-card" style={{ margin: '0 auto', textAlign: 'left' }}>
                  <h2 style={{ fontSize: 16 }}>Your progress is saved</h2>
                  <p style={{ fontSize: 14 }}>{resumePanel.message}</p>
                  <div className="ob-resume-box">{resumePanel.link}</div>
                  <button
                    type="button"
                    className="ob-btn ob-btn-ghost"
                    style={{ marginTop: 14, width: '100%' }}
                    onClick={() => setResumePanel(null)}
                  >
                    Keep going
                  </button>
                </div>
              )}

              {resumePanel?.state === 'error' && (
                <p className="ob-note" style={{ justifyContent: 'center' }}>{resumePanel.message}</p>
              )}
            </div>
          )}
        </div>

        {!isWelcome && !isComplete && (
          <ProfileRail
            steps={dataSteps}
            currentKey={stepKey}
            sections={sections}
            onJump={goToFix}
          />
        )}
      </main>

      {!isComplete && (
        <footer className="ob-footer">
          <div className="ob-footer-inner">
            {index > 0 && (
              <button type="button" className="ob-btn ob-btn-ghost" onClick={handleBack} disabled={busy}>
                <CaretLeft size={15} weight="bold" />
                <span className="ob-sr-only">Go to previous step</span>
                Back
              </button>
            )}

            <span className="ob-footer-spacer" />

            {step.optional && (
              <button type="button" className="ob-btn ob-btn-text" onClick={handleSkip} disabled={busy}>
                Skip for now
              </button>
            )}

            <button
              type="button"
              className="ob-btn ob-btn-primary"
              onClick={handleContinue}
              disabled={busy}
            >
              {busy && <CircleNotch size={15} className="ob-spin" />}
              {!busy && continueLabel}
              {busy && (isReview ? 'Sending…' : 'Saving…')}
              {!busy && (isReview
                ? <PaperPlaneTilt size={15} weight="fill" />
                : <CaretRight size={15} weight="bold" />)}
            </button>
          </div>
        </footer>
      )}

      <MilestoneToast message={milestone} />
    </div>
  );
}
